"use client";

import { useState, useEffect, lazy, Suspense } from "react";
import { allowCheckout } from "@/app/_module/utils/nativeCheckoutGuard";
import { isNativePlatform } from "@/lib/capacitor/platform";
import { useRouter, useSearchParams } from "next/navigation";
import { Search,
  Phone,
  Plus,
  Settings,
  ShoppingCart,
  BarChart3,
  TrendingUp,
  MapPin,
  DollarSign,
  AlertTriangle,
  RefreshCw,
  Target,
  CheckCircle,
  Sparkles,
  Crosshair,
  MessageSquare,
  Clock,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
  ArrowUpRight,
  ArrowDownLeft,
  Calendar,
  Trash2,
  UserCog,
  Shield,
  ShieldCheck,
  Activity,
  Bot,
  Zap,
  Users,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Button } from "@/app/_module/components/ui/button";
import { Badge } from "@/app/_module/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/app/_module/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/app/_module/components/ui/tabs";
import { Dialog, ResponsiveDialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/app/_module/components/ui/dialog";
import { useToast } from "@/app/_module/hooks/use-toast";
import { Switch } from "@/app/_module/components/ui/switch";
import { Label } from "@/app/_module/components/ui/label";
import { Input } from "@/app/_module/components/ui/input";
import { Separator } from "@/app/_module/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/app/_module/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/_module/components/ui/select";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { DialerSystemChoice } from "./DialerSystemChoice";
import { DIALER_SYSTEMS, carrierOf, type DialerCarrier } from "@/app/_module/utils/dialerSystem";
import { getAdminAccess } from "@/app/_module/utils/admin-access";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { VerifyPurchasesResult, type VerifyPurchasesData } from "./VerifyPurchasesResult";
import { AreaCodeSubstitutionNote } from "./AreaCodeSubstitutionNote";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { usePhoneNumberManager, phoneNumberQueryKeys } from "@/app/_module/hooks/usePhoneNumberPricing";
import { PhoneNumberPricingDisplay } from "@/app/_module/components/PhoneNumberPricingDisplay";
import { PhoneNumberSelector } from "./PhoneNumberSelector";
import { AreaCodePurchaseGrid } from "./AreaCodePurchaseGrid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { phoneNumberPricingService } from "@/app/_module/services/phoneNumberPricingService";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";
import { Loader2 } from "lucide-react";
import { isDemoMode } from "@/app/_module/utils/demoMode";

const NumberHealthDashboard = lazy(() => import("./NumberHealthDashboard"));

// Phone number interface for settings modal
interface PhoneNumber {
  id: string;
  phoneNumber: string;
  areaCode?: string;
  state?: string;
  city?: string;
  isActive?: boolean;
  assignedTo?: {
    id: string;
    name?: string;
    email?: string;
  };
}

// A2P Verification Status interface
interface A2PVerificationStatus {
  isVerified: boolean;
  status: 'verified' | 'pending' | 'unverified' | 'rejected';
  brandId?: string;
  campaignId?: string;
  verifiedAt?: string;
  messagingLimit?: number;
}

// Types based on backend API specifications
interface AreaCodeInsights {
  coverageAnalysis: {
    totalNumbers: number;
    uniqueAreaCodes: number;
    statesCovered: number;
    coverageScore: number;
  };
  topMissingAreaCodes: Array<{
    areaCode: string;
    state: string;
    city?: string;
    priority: 'HIGH' | 'MEDIUM' | 'LOW';
    estimatedCost: number;
    reason: string;
    frequency: number;
    lastCallDate: string;
    potentialMatches: number;
  }>;
  recentCallsWithPoorMatches: {
    total: number;
    averageMatchScore: number;
    missedOpportunities: number;
  };
  costBenefit: {
    estimatedImprovementPercentage: number;
    totalRecommendedInvestment: number;
    potentialROI: string;
  };
}

interface Recommendations {
  summary: string;
  actionItems: string[];
  urgentActions: Array<{
    areaCode: string;
    priority: 'HIGH';
    reason: string;
  }>;
}

interface AreaCodeRecommendation {
  areaCode: string;
  state: string;
  city?: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  estimatedCost: number;
  reason: string;
  frequency: number;
  lastCallDate: string;
  potentialMatches: number;
}

// Stripe Checkout allows a maximum of 20 line items, and we create one line
// item per number — so a single purchase is capped at 20 numbers. The UI
// enforces this so users don't build a 21+ cart that the checkout would reject.
const MAX_NUMBERS_PER_PURCHASE = 20;

// NUMBER SEARCH helper (2026-07-24): matches digits in any formatting, area
// code, state, and assigned/owner name. Used by all three Manage lists.
const numberMatches = (phone: any, q: string): boolean => {
  if (!q.trim()) return true;
  const needle = q.trim().toLowerCase();
  const needleDigits = needle.replace(/\D/g, '');
  const num = String(phone.number || phone.phone_number || phone.phoneNumber || '');
  const hay = [
    num,
    String(phone.areaCode || phone.area_code || ''),
    String(phone.state?.name || phone.state?.code || phone.state || ''),
    String(phone.assignedToName || phone.assigned_to_name || phone.owner?.name || phone.assignedUser?.name || ''),
  ].join(' ').toLowerCase();
  if (needleDigits && num.replace(/\D/g, '').includes(needleDigits)) return true;
  return hay.includes(needle);
};

/** Free signup numbers are untouchable until they lapse (owner rule 2026-09-01):
 *  no Remove, no Delete-now. Backend refuses both as well. */
const isProtectedFreeNumber = (ph: any): boolean => {
  const exp = ph?.freeTrialExpiresAt || ph?.free_trial_expires_at;
  return !!(ph?.isFreeTrialNumber || ph?.is_free_trial_number) && !!exp && new Date(exp).getTime() > Date.now();
};

export function UnifiedAreaCodeManagement() {
  const { toast } = useToast();
  const { user } = useUserContext();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Get area code sub-tabs from URL
  const areaCodeTab = searchParams.get("areaCodeTab") || "management";

  // InsuraDialer system new numbers are bought on: picked here (or by ?system= from the
  // Dialer settings switch), else the agent's current one. Offered only when Next exists.
  const nextDialerAvailable = !!(user?.user as any)?.nextDialerAvailable;
  const currentDialer = carrierOf((user?.user as any)?.voiceProvider);
  const [buyPick, setBuyPick] = useState<DialerCarrier | null>(() => {
    const s = searchParams.get("system");
    return s === "telnyx" || s === "twilio" ? s : null;
  });
  const buySystem = buyPick ?? currentDialer;
  const dialerBadge = (provider?: string | null) =>
    nextDialerAvailable && provider ? (
      <Badge
        variant="outline"
        className={`text-xs ${carrierOf(provider) === "telnyx" ? "border-blue-600 text-blue-700" : "text-gray-600"}`}
        title={DIALER_SYSTEMS[carrierOf(provider)].name}
      >
        {DIALER_SYSTEMS[carrierOf(provider)].short}
      </Badge>
    ) : null;

  const activityTab = searchParams.get("activityTab") || "calls";

  // Store compliance: opening the Buy Area Codes tab inside the native app pops
  // the "available on the web version" explainer. No-op on web.
  useEffect(() => {
    if (areaCodeTab === "purchase") allowCheckout("area-code");
  }, [areaCodeTab]);

  // …and the tab's CONTENT is replaced too: dismissing the popup must not
  // leave a browsable purchase grid in the app (state, not a render-time
  // check, so SSR/desktop hydration stays consistent).
  const [isNativeApp, setIsNativeApp] = useState(false);
  useEffect(() => {
    setIsNativeApp(isNativePlatform());
  }, []);

  // Returning from Stripe. The backend sends ?status=cancelled or ?status=success.
  // Clear the selection on BOTH: on success it is already bought, on cancel it is
  // abandoned — and leaving it set is what made the next checkout carry codes the
  // user thought they had walked away from. Also strips the param so a refresh
  // does not replay the toast.
  useEffect(() => {
    const status = searchParams.get("status");
    if (status !== "cancelled" && status !== "success") return;
    setSelectedAreaCodes(new Set());
    toast(
      status === "cancelled"
        ? { title: "Checkout cancelled", description: "Nothing was purchased and your selection has been cleared." }
        : { title: "Purchase complete", description: "Your numbers are being set up." },
    );
    const params = new URLSearchParams(searchParams.toString());
    params.delete("status");
    params.delete("session_id");
    router.replace(`/settings?${params.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle area code main tab change
  const handleAreaCodeTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("areaCodeTab", value);
    router.replace(`/settings?tab=area-codes&${params.toString()}`, { scroll: false });
  };

  // Handle activity sub-tab change
  const handleActivityTabChange = (value: string) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("activityTab", value);
    router.replace(`/settings?tab=area-codes&${params.toString()}`, { scroll: false });
  };

  // Check user role and agency membership
  const adminAccess = getAdminAccess(
    user?.user?.userType || '', 
    user?.currentAgency?.role
  );
  const isAgencyOwner = !!(adminAccess.isAgencyOwner || adminAccess.isAgencyAdmin);
  const isAgencyMember = !!user?.currentAgency?.agency?.id; // User is part of an agency
  const isInvitedAgent = isAgencyMember && !isAgencyOwner; // Agent invited to agency
  const [verifyResult, setVerifyResult] = useState<VerifyPurchasesData | null>(null);
  const [substituteConfirm, setSubstituteConfirm] = useState<{
    url: string;
    items: Array<{ areaCode: number; likelyAreaCodes: number[] }>;
  } | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [insights, setInsights] = useState<AreaCodeInsights | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendations | null>(null);
  const [highPriorityRecs, setHighPriorityRecs] = useState<AreaCodeRecommendation[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState<string | null>(null);
  const [showPhoneNumberSelector, setShowPhoneNumberSelector] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [selectedAreaCodes, setSelectedAreaCodes] = useState<Set<string>>(new Set());
  // Bulk multi-select removal of owned numbers
  const [selectedNumberIds, setSelectedNumberIds] = useState<Set<string>>(new Set());
  // Search boxes for the three Manage lists (2026-07-24)
  const [mySearch, setMySearch] = useState('');
  const [agencySearch, setAgencySearch] = useState('');
  const [deactivatedSearch, setDeactivatedSearch] = useState('');
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const [bulkRemoveConfirm, setBulkRemoveConfirm] = useState(false);
  const [phoneSettingsModal, setPhoneSettingsModal] = useState<{
    isOpen: boolean;
    phone: PhoneNumber | null;
  }>({ isOpen: false, phone: null });
  // Deactivated numbers now use React Query for live updates (removed useState)
  const [isRecovering, setIsRecovering] = useState<string | null>(null);
  const [showTransferDropdown, setShowTransferDropdown] = useState(false);
  const [selectedTransferAgent, setSelectedTransferAgent] = useState<string>('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [showAgencyView, setShowAgencyView] = useState(false);

  // Enhanced modal state
  const [phoneStats, setPhoneStats] = useState<any>(null);
  const [phoneBillingInfo, setPhoneBillingInfo] = useState<any>(null);
  const [loadingPhoneStats, setLoadingPhoneStats] = useState(false);
  const [recentCalls, setRecentCalls] = useState<any[]>([]);
  const [recentSms, setRecentSms] = useState<any[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [phoneSettings, setPhoneSettings] = useState({
    callForwarding: false,
    voicemail: true,
    doNotDisturb: false,
    customCallerIdName: ''
  });

  // AI Area Code Matching Preferences
  const [matchingStrategy, setMatchingStrategy] = useState<'exact' | 'metropolitan' | 'nearest'>('nearest');
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);
  const queryClient = useQueryClient();

  // Get phone number manager hook - must be called before useEffect that uses refreshPhoneNumbers
  const {
    pricingInfo,
    phoneNumbers,
    isPhoneNumbersLoading,
    phoneNumbersError,
    assignPhoneNumber,
    isAssigning,
    canAddPhoneNumber,
    refreshPhoneNumbers,
  } = usePhoneNumberManager();

  // Handle Stripe redirect success/cancel
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const sessionId = urlParams.get('session_id');

    if (status === 'success' && sessionId) {
      // Call fulfillment API to provision phone numbers
      const fulfillCheckout = async () => {
        try {
          const result = await handleApiCall<any>(async () => {
            return await api.post({
              url: getApiUrl(`/area-codes/checkout/fulfill/${sessionId}`),
              auth: true
            });
          });
          const response = result?.data || result;

          // Aggressively invalidate ALL phone number related queries FIRST
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.all });
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.pricing() });
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
          queryClient.invalidateQueries({ queryKey: ["availablePhoneNumbers"] });
          queryClient.invalidateQueries({ queryKey: ['agencyPhoneAssignments'] });
          
          // Add a small delay to allow backend to process the fulfillment
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Force refetch phone numbers immediately
          await queryClient.refetchQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
          await queryClient.refetchQueries({ queryKey: phoneNumberQueryKeys.pricing() });
          
          // Also call the refresh function for good measure
          await refreshPhoneNumbers();

          if (response && response.provisionedNumbers && response.provisionedNumbers.length > 0) {
            toast({
              title: "Purchase Successful!",
              description: `${response.provisionedNumbers.length} phone number(s) provisioned successfully!`,
              duration: 6000,
            });
          } else {
            toast({
              title: "Purchase Successful!",
              description: "Your area codes have been purchased. Phone numbers are being provisioned.",
              duration: 6000,
            });
          }

          // Invalidate purchase history to show new transaction
          queryClient.invalidateQueries({ queryKey: ["purchaseHistory"] });
          queryClient.invalidateQueries({ queryKey: ["active-subscription"] });
          queryClient.invalidateQueries({ queryKey: ["my-active-subscription"] });
        } catch (error) {

          // Even on error, invalidate queries to try to refresh
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.all });
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.pricing() });
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
          
          // Try to refetch after a delay
          setTimeout(async () => {
            await queryClient.refetchQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
            await refreshPhoneNumbers();
          }, 2000);
          
          toast({
            title: "Purchase Complete",
            description: "Your payment was successful. Phone numbers will be provisioned shortly.",
            duration: 6000,
          });

          // Still invalidate queries even on error
          queryClient.invalidateQueries({ queryKey: ["purchaseHistory"] });
        }
      };

      fulfillCheckout();

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=area-codes');
      // Switch to management tab programmatically if needed
      const tabTrigger = document.querySelector('[value="management"]') as HTMLElement;
      if (tabTrigger) {
        tabTrigger.click();
      }
    } else if (status === 'recovery_success' && sessionId) {
      // Call recovery fulfillment API (fallback for webhook)
      const fulfillRecovery = async () => {
        try {
          const result = await handleApiCall<any>(async () => {
            return await api.post({
              url: getApiUrl(`/stripe/recovery/fulfill/${sessionId}`),
              auth: true
            });
          });
          const response = result?.data || result;

          // Aggressively invalidate ALL phone number related queries
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.all });
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.pricing() });
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
          queryClient.invalidateQueries({ queryKey: ['deactivatedPhoneNumbers'] });
          queryClient.invalidateQueries({ queryKey: ['agencyPhoneNumbers'] });
          queryClient.invalidateQueries({ queryKey: ['agencyPhoneAssignments'] });

          // Add a small delay to allow backend to process
          await new Promise(resolve => setTimeout(resolve, 1000));

          // Force refetch phone numbers immediately
          await queryClient.refetchQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
          await queryClient.refetchQueries({ queryKey: ['deactivatedPhoneNumbers'] });

          // Also call the refresh function for good measure
          await refreshPhoneNumbers();

          if (response?.recovered || response?.alreadyRecovered) {
            toast({
              title: "Phone Number Recovered!",
              description: `Your phone number ${response.phoneNumber ? `(${response.phoneNumber})` : ''} has been successfully recovered and reactivated.`,
              duration: 6000,
            });
          } else {
            toast({
              title: "Phone Number Recovered!",
              description: "Your phone number has been successfully recovered and reactivated.",
              duration: 6000,
            });
          }

          // Invalidate purchase history to show new transaction
          queryClient.invalidateQueries({ queryKey: ["purchaseHistory"] });
        } catch (error) {
          // Even on error, try to refresh - webhook might have handled it
          queryClient.invalidateQueries({ queryKey: phoneNumberQueryKeys.all });
          queryClient.invalidateQueries({ queryKey: ['deactivatedPhoneNumbers'] });

          // Try to refetch after a delay
          setTimeout(async () => {
            await queryClient.refetchQueries({ queryKey: phoneNumberQueryKeys.userNumbers() });
            await queryClient.refetchQueries({ queryKey: ['deactivatedPhoneNumbers'] });
            await refreshPhoneNumbers();
          }, 2000);

          toast({
            title: "Recovery Complete",
            description: "Your payment was successful. Phone number will be reactivated shortly.",
            duration: 6000,
          });

          // Still invalidate queries even on error
          queryClient.invalidateQueries({ queryKey: ["purchaseHistory"] });
        }
      };

      fulfillRecovery();

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=area-codes');
    } else if (status === 'recovery_success') {
      // Fallback if no sessionId (shouldn't happen but handle gracefully)
      toast({
        title: "Phone Number Recovered!",
        description: "Your phone number has been successfully recovered and reactivated.",
        duration: 6000,
      });
      // Refresh all phone number lists
      refreshPhoneNumbers();
      queryClient.invalidateQueries({ queryKey: ['deactivatedPhoneNumbers'] });
      queryClient.invalidateQueries({ queryKey: ['agencyPhoneNumbers'] });
      queryClient.invalidateQueries({ queryKey: ["purchaseHistory"] });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=area-codes');
    } else if (status === 'cancelled') {
      toast({
        title: "Purchase Cancelled",
        description: "You cancelled the area code purchase.",
        variant: "destructive",
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=area-codes');
    } else if (status === 'recovery_cancelled') {
      toast({
        title: "Recovery Cancelled",
        description: "You cancelled the phone number recovery.",
        variant: "destructive",
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=area-codes');
    }
  }, [toast, queryClient, refreshPhoneNumbers]);

  // Fetch available phone numbers from backend
  const { data: availableNumbers = [], isLoading: isLoadingAvailable } = useQuery({
    queryKey: ['availablePhoneNumbers'],
    queryFn: () => phoneNumberPricingService.getAvailablePhoneNumbers({ limit: 2000 }),
    enabled: showPhoneNumberSelector, // Only fetch when dialog is open
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Agency phone number assignments (for owners/admins) - uses dedicated endpoint with hierarchy support
  const currentAgencyId = user?.currentAgency?.agency?.id || "";
  const { data: assignedAgencyNumbers = [], isLoading: isAgencyLoading } = useQuery({
    queryKey: ['agencyPhoneAssignments', currentAgencyId],
    queryFn: async () => {
      const response = await api.get({
        url: getApiUrl(endpoint.communication.getPhoneNumbers + '/agency-assignments?includeHierarchy=true'),
        auth: true,
      });
      const data = (response as any)?.data?.data || (response as any)?.data || [];
      return Array.isArray(data) ? data : [];
    },
    enabled: isAgencyOwner && !!currentAgencyId,
    staleTime: 60_000, // 60 seconds
    refetchInterval: 60_000, // Refetch every 60s for number updates
  });
  const agencyCount = assignedAgencyNumbers.length;

  // Agency numbers grouped by agent (for hierarchy view)
  const { data: agencyNumbersGrouped = [], isLoading: isAgencyNumbersLoading } = useQuery({
    queryKey: ['agencyNumbers', currentAgencyId],
    queryFn: async () => {
      const response = await api.get({
        url: getApiUrl(endpoint.communication.getPhoneNumbers + '/agency-numbers'),
        auth: true,
      });
      const data = (response as any)?.data?.data || (response as any)?.data || [];
      return Array.isArray(data) ? data : [];
    },
    enabled: isAgencyOwner && !!currentAgencyId && showAgencyView,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  // Deactivated numbers query - with live updates
  const { data: deactivatedNumbers = [], isLoading: isLoadingDeactivated } = useQuery({
    queryKey: ['deactivatedPhoneNumbers'],
    queryFn: async () => {
      const response = await api.get({
        url: getApiUrl(endpoint.communication.getPhoneNumbers + '/deactivated'),
        auth: true,
      });
      const data = (response as any)?.data?.data || (response as any)?.data || [];
      return Array.isArray(data) ? data : [];
    },
    staleTime: 60_000, // 60 seconds
    refetchInterval: 60_000, // Refetch every 60s for deactivated numbers
  });

  // Fetch A2P verification status
  const { data: a2pStatus, isLoading: isA2PLoading } = useQuery<A2PVerificationStatus>({
    queryKey: ['a2p-status'],
    queryFn: async () => {
      const response = await api.get<{ statusCode: number; data: A2PVerificationStatus }>({
        url: getApiUrl("/agency/a2p/my-status"),
        auth: true,
      });
      const data = (response as any)?.data?.data || (response as any)?.data;
      return data || { isVerified: false, status: 'unverified' };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // Fetch A2P verified agents in the agency for transfer
  const { data: verifiedAgents = [], isLoading: isLoadingAgents } = useQuery({
    queryKey: ['verified-agents', currentAgencyId],
    queryFn: async () => {
      if (!currentAgencyId || !isAgencyOwner) return [];

      const response = await api.get({
        url: getApiUrl(`/agency/${currentAgencyId}/members/a2p-verified`),
        auth: true,
      });

      const data = (response as any)?.data?.data || (response as any)?.data || [];
      return Array.isArray(data) ? data : [];
    },
    enabled: isAgencyOwner && !!currentAgencyId && showTransferDropdown,
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    fetchData();
    // fetchDeactivatedNumbers is now handled by React Query
    fetchAreaCodePreferences();
  }, []);

  const fetchAreaCodePreferences = async () => {
    try {
      setIsLoadingPreferences(true);
      const response = await handleApiCall(async () => {
        return await api.get({
          url: getApiUrl(endpoint.areaCodePreferences.get),
          auth: true
        });
      });

      if (response && response.matchingStrategy) {
        setMatchingStrategy(response.matchingStrategy);
        if (typeof window !== 'undefined') {
          localStorage.setItem('dialerMatchingStrategy', response.matchingStrategy);
        }
      }
    } catch (error) {

      // Silently fail - use default 'nearest' strategy
    } finally {
      setIsLoadingPreferences(false);
    }
  };

  const handleStrategyToggle = async (strategy: 'exact' | 'metropolitan' | 'nearest') => {
    try {
      setIsSavingPreferences(true);

      const response = await handleApiCall(async () => {
        return await api.put({
          url: getApiUrl(endpoint.areaCodePreferences.updateMatchingStrategy),
          body: { strategy },
          auth: true
        });
      });

      if (response) {
        setMatchingStrategy(strategy);
        if (typeof window !== 'undefined') {
          localStorage.setItem('dialerMatchingStrategy', strategy);
          window.dispatchEvent(new CustomEvent('dialerStrategyChanged', { detail: { strategy } }));
        }
        toast({
          title: "Strategy Updated",
          description: `AI matching strategy set to: ${strategy}`,
        });
      }
    } catch (error) {

      toast({
        title: "Update Failed",
        description: "Failed to update AI matching strategy. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPreferences(false);
    }
  };

  // fetchDeactivatedNumbers removed - now using React Query with live updates

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Real API calls with proper error handling
      const [insightsRes, recsRes] = await Promise.all([
        handleApiCall<{ insights: AreaCodeInsights; recommendations: Recommendations }>(async () => {
          const response = await api.get<{ statusCode: number; data: { insights: AreaCodeInsights; recommendations: Recommendations } }>({
            url: getApiUrl(endpoint.communication.smartAreaCodeInsights)
          });
          return response;
        }),
        handleApiCall<{ recommendations: { high: AreaCodeRecommendation[] } }>(async () => {
          const response = await api.get<{ statusCode: number; data: { recommendations: { high: AreaCodeRecommendation[] } } }>({
            url: getApiUrl(endpoint.communication.areaCodePurchaseRecommendations)
          });
          return response;
        })
      ]);

      if (insightsRes && insightsRes.insights && insightsRes.recommendations) {
        setInsights(insightsRes.insights);
        setRecommendations(insightsRes.recommendations);
      } else {
        // Set empty defaults if no data
        setInsights(null);
        setRecommendations(null);
      }

      if (recsRes && recsRes.recommendations && recsRes.recommendations.high) {
        setHighPriorityRecs(Array.isArray(recsRes.recommendations.high) ? recsRes.recommendations.high : []);
      } else {
        // Set empty array if no recommendations
        setHighPriorityRecs([]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'production') {

      }
      toast({
        title: "Error Loading Data",
        description: "Failed to load area code insights. Please try again later.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  };

  const handleAreaCodeSelect = (areaCode: number, state: string, agentId?: string) => {
    const key = `${areaCode}-${state}`;
    setSelectedAreaCodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        if (newSet.size >= MAX_NUMBERS_PER_PURCHASE) {
          toast({
            title: `Up to ${MAX_NUMBERS_PER_PURCHASE} numbers per purchase`,
            description: `Buy these ${MAX_NUMBERS_PER_PURCHASE} first, then start another batch for more.`,
            variant: "destructive",
          });
          return prev;
        }
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleAreaCodePurchase = async (areaCode: number, state: string, agentId?: string) => {
    try {
      setIsPurchasing(`${areaCode}-${state}`);
      
      // Create Stripe checkout session for area code purchase
      const response = await handleApiCall<{ url: string }>(async () => {
        const res = await api.post({
          url: getApiUrl(endpoint.stripe.checkoutSessions),
          body: {
            items: [{
              type: 'area_code',
              areaCode: areaCode,
              state: state,
              ...(agentId && { assignedTo: agentId })
            }],
            successUrl: `${window.location.origin}/settings?tab=area-codes&success=true`,
            cancelUrl: `${window.location.origin}/settings?tab=area-codes&cancelled=true`
          },
          auth: true
        });
        return res;
      });

      if (response && typeof response === 'object' && 'url' in response && response.url) {
        // Redirect to Stripe checkout
        window.location.href = response.url as string;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {

      toast({
        title: "Purchase Failed",
        description: error.response?.data?.message || "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsPurchasing(null);
    }
  };

  const handlePurchaseAreaCode = async (areaCode: string) => {
    try {
      setIsPurchasing(areaCode);
      
      const rec = highPriorityRecs.find(r => r.areaCode === areaCode);
      
      // Stripe checkout integration
      const response = await handleApiCall<{ url: string }>(async () => {
        const res = await api.post<{ statusCode: number; data: { url: string } }>({
          url: getApiUrl(endpoint.communication.startAreaNumberCheckout),
          body: {
            areaCodes: [areaCode],
            state: rec?.state || 'CA',
            recommendationContext: {
              priority: rec?.priority || 'HIGH',
              expectedCalls: rec?.frequency || 0,
              reason: 'smart_recommendation'
            }
          }
        });
        return res;
      });

      if (response && response.url) {
        window.location.href = response.url; // Redirect to Stripe
      } else {
        toast({
          title: 'Purchase Initiated',
          description: `Starting checkout for area code ${areaCode}`,
        });
        await handleRefresh();
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'production') {

      }
      toast({
        title: 'Purchase Failed',
        description: 'Failed to initiate area code purchase. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsPurchasing(null);
    }
  };

  const handleAddPhoneNumber = () => {
    setShowPhoneNumberSelector(true);
  };

  const handlePhoneNumberSelected = (phoneNumber: any) => {
    // Extract area code from the phone number - handle both formatted and raw numbers
    let areaCode: number;
    
    if (phoneNumber.areaCode) {
      // If areaCode is already provided, use it
      areaCode = parseInt(phoneNumber.areaCode.toString());
    } else if (phoneNumber.number) {
      // Extract from phone number string
      const cleanedNumber = phoneNumber.number.replace(/\D/g, '');
      if (cleanedNumber.length >= 10) {
        // Take first 3 digits for area code
        areaCode = parseInt(cleanedNumber.substring(0, 3));
      } else {
        throw new Error('Invalid phone number format');
      }
    } else {
      throw new Error('No phone number or area code provided');
    }
    
    // Validate area code is a valid 3-digit number
    if (isNaN(areaCode) || areaCode < 200 || areaCode > 999) {
      throw new Error('Invalid area code format');
    }
    
    assignPhoneNumber({ 
      areaCode, 
      state: phoneNumber.state || phoneNumber.stateCode || 'Unknown'
    });
    
    setShowPhoneNumberSelector(false);
    
    toast({
      title: "Phone Number Assigned",
      description: `Successfully assigned ${phoneNumber.number}`,
    });
  };

  const handleBulkAreaCodeCheckout = async (interval: 'month' | 'year', agentId?: string) => {
    // DEMO SANDBOX: offer the trial at the click.
    //
    // The backend blocks this checkout anyway (Stripe proxy + assertNotDemo on the
    // purchase path) — but only after the visitor has picked area codes, chosen an
    // agent and a billing interval, and pressed Checkout, at which point a red failure
    // toast reads as a bug rather than a boundary. Buying numbers is also the single
    // most real-money action in the product, which makes this exact click the right
    // moment for the offer. Same pattern as Leads export and Master Export.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            'Purchasing phone numbers is disabled in the demo sandbox. Start a free trial to buy real numbers for your agency.',
        }),
      );
      return;
    }

    try {
      // Defense-in-depth: the checkout (one Stripe line item per number) maxes at 20.
      if (selectedAreaCodes.size > MAX_NUMBERS_PER_PURCHASE) {
        toast({
          title: `Too many numbers`,
          description: `You can buy up to ${MAX_NUMBERS_PER_PURCHASE} at once. Remove ${selectedAreaCodes.size - MAX_NUMBERS_PER_PURCHASE} to continue.`,
          variant: "destructive",
        });
        return;
      }
      setIsPurchasing('bulk');

      // Convert selectedAreaCodes Set to array of objects
      const areaCodesArray = Array.from(selectedAreaCodes).map(key => {
        const [areaCode, state] = key.split('-');
        return {
          areaCode,
          stateId: state // You may need to look up stateId from state name
        };
      });

      if (areaCodesArray.length === 0) {
        toast({
          title: "No Area Codes Selected",
          description: "Please select at least one area code to purchase.",
          variant: "destructive",
        });
        return;
      }

      // Call the new bulk checkout endpoint
      const response = await handleApiCall<{ checkoutUrl: string; sessionId: string; willSubstitute?: Array<{ areaCode: number; likelyAreaCodes: number[] }> }>(async () => {
        const res = await api.post<{ statusCode: number; data: { checkoutUrl: string; sessionId: string; willSubstitute?: Array<{ areaCode: number; likelyAreaCodes: number[] }> } }>({
          url: getApiUrl(endpoint.areaCodes.checkout),
          body: {
            areaCodes: areaCodesArray,
            interval: interval,
            ...(agentId && { assignedTo: agentId }),
            ...(nextDialerAvailable && { carrier: buySystem }),
          },
          auth: true
        });
        return res;
      });

      if (response && response.checkoutUrl) {
        // The server checked Twilio LIVE during checkout creation. If any code is
        // out of stock and will be fulfilled by a same-metro overlay, say so
        // before sending them to pay — ordering 843 and silently receiving an 854
        // is what generated the complaint on 2026-08-18. The Stripe session is
        // already created but nothing is charged until they complete it, so
        // pausing here costs nothing.
        const subs = (response as any).willSubstitute as
          | Array<{ areaCode: number; likelyAreaCodes: number[] }>
          | undefined;
        if (subs && subs.length > 0) {
          setSubstituteConfirm({ url: response.checkoutUrl, items: subs });
          return;
        }
        // The selection is now encoded in the Stripe session, so it has been
        // spent. Leaving it set is what let an ABANDONED checkout leak into the
        // next one: pick 216, back out, pick 202, and checkout sent both — or
        // just the stale 216. Nothing cleared it on redirect, on return, or on
        // cancel.
        setSelectedAreaCodes(new Set());
        (allowCheckout("area-code") ? (window.location.href = response.checkoutUrl) : undefined);
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {

      toast({
        title: "Checkout Failed",
        description: error.response?.data?.message || "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsPurchasing(null);
    }
  };

  const handleRemovePhoneNumber = async (phoneId: string) => {
    // DEMO SANDBOX: cancelling a number is a real Twilio release and a real Stripe subscription change.
    // The seeded numbers are what make the dialer, caller ID and area-code matching work,
    // so a visitor releasing them would break those surfaces for the NEXT visitor.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Cancelling a phone number is disabled in the demo sandbox. Start a free trial to manage your real numbers.",
        }),
      );
      return;
    }

    if (!window.confirm("Cancel this number? You'll keep using it until the end of the current billing period, then it releases at renewal. Billing stops now — no next charge.")) {
      return;
    }

    setIsRemoving(phoneId);
    try {
      // Same cancel-at-period-end flow as the bulk action: keep the number usable
      // until the paid period ends, stop the renewal, then a cron releases it.
      const response: any = await api.post({
        url: getApiUrl(endpoint.communication.getPhoneNumbers + '/bulk-remove'),
        body: { phoneIds: [phoneId] },
      });
      const payload = response?.data ?? response;
      const inner = payload?.data ?? payload ?? {};

      if (payload?.statusCode === 200) {
        await refreshPhoneNumbers();
        const activeUntil = inner.activeUntil ? new Date(inner.activeUntil).toLocaleDateString() : null;
        toast({
          title: "Number scheduled to cancel",
          description: activeUntil ? `Active until ${activeUntil}, then released.` : "Active until renewal, then released.",
        });
      } else {
        throw new Error('Failed to cancel phone number');
      }
    } catch (error) {

      toast({
        title: "Error",
        description: "Failed to cancel the number. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRemoving(null);
    }
  };

  // --- Bulk multi-select removal (owned numbers only) ---
  const canBulkSelect = (phone: any) => phone?.isOwned !== false && !!phone?.id;
  const bulkSelectableNumbers: any[] = (phoneNumbers || []).filter(canBulkSelect);
  const allBulkSelected = bulkSelectableNumbers.length > 0 && bulkSelectableNumbers.every((p: any) => selectedNumberIds.has(p.id));
  const toggleNumberSelect = (phoneId: string) => {
    setSelectedNumberIds((prev) => {
      const next = new Set(prev);
      if (next.has(phoneId)) next.delete(phoneId); else next.add(phoneId);
      return next;
    });
  };
  const toggleSelectAllNumbers = () => {
    setSelectedNumberIds(allBulkSelected ? new Set() : new Set(bulkSelectableNumbers.map((p: any) => p.id)));
  };
  const executeBulkRemoveNumbers = async () => {
    const phoneIds = Array.from(selectedNumberIds);
    if (phoneIds.length === 0) return;
    setBulkRemoveConfirm(false);
    setIsBulkRemoving(true);
    try {
      const response: any = await api.post({
        url: getApiUrl(endpoint.communication.getPhoneNumbers + '/bulk-remove'),
        body: { phoneIds, immediate: bulkImmediate && bulkImmediateConfirm.trim().toUpperCase() === 'DELETE' },
      });
      const payload = response?.data ?? response;
      const inner = payload?.data ?? payload ?? {};
      const scheduled = inner.scheduled ?? 0;
      const failed = Array.isArray(inner.failed) ? inner.failed.length : 0;
      const saved = inner.monthlySavingsUsd ?? 0;
      const activeUntil = inner.activeUntil ? new Date(inner.activeUntil).toLocaleDateString() : null;
      await refreshPhoneNumbers();
      setSelectedNumberIds(new Set());
      const wasImmediate = inner.immediate === true;
      toast({
        title: wasImmediate
          ? `${scheduled} number${scheduled !== 1 ? 's' : ''} releasing now`
          : `${scheduled} number${scheduled !== 1 ? 's' : ''} scheduled to cancel`,
        description: wasImmediate
          ? `They stop ringing immediately and can't be recovered. ${saved > 0 ? `Won't renew (−$${saved}/mo). ` : ''}${failed > 0 ? `${failed} failed.` : ''}`
          : `${activeUntil ? `Active until ${activeUntil}, then released. ` : 'Active until renewal, then released. '}${saved > 0 ? `Won't renew (−$${saved}/mo). ` : ''}${failed > 0 ? `${failed} failed.` : ''}`,
      });
      setBulkImmediate(false);
      setBulkImmediateConfirm("");
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove the selected numbers. Please try again.", variant: "destructive" });
    } finally {
      setIsBulkRemoving(false);
    }
  };

  // --- Instant release ("Delete now" — the spam-number escape hatch) ---
  // Unlike the normal cancel (number stays live until renewal), this releases
  // the number at the carrier IMMEDIATELY. Confirmation is typing the exact
  // number; the backend re-verifies it digit-for-digit before acting.
  const [bulkImmediate, setBulkImmediate] = useState(false);
  const [bulkImmediateConfirm, setBulkImmediateConfirm] = useState("");
  const [instantDeleteTarget, setInstantDeleteTarget] = useState<any | null>(null);
  const [instantDeleteInput, setInstantDeleteInput] = useState("");
  const [isInstantDeleting, setIsInstantDeleting] = useState(false);
  // Last-10-digit comparison (mirrors the backend): stored numbers are E.164
  // (+1XXXXXXXXXX) but agents type "(999) 555-0099" — both must pass.
  const instantDeleteDigits = (v: string) =>
    (v || "").replace(/\D/g, "").replace(/^1(?=\d{10}$)/, "");
  const instantDeleteMatches =
    !!instantDeleteTarget &&
    instantDeleteDigits(instantDeleteInput).length >= 10 &&
    instantDeleteDigits(instantDeleteInput) ===
      instantDeleteDigits(String(instantDeleteTarget.phoneNumber || ""));

  const executeInstantDelete = async () => {
    if (!instantDeleteTarget || !instantDeleteMatches) return;
    setIsInstantDeleting(true);
    try {
      const response: any = await api.post({
        url: getApiUrl(endpoint.communication.getPhoneNumbers + `/${instantDeleteTarget.id}/release-now`),
        body: { confirmNumber: instantDeleteInput },
      });
      const payload = response?.data ?? response;
      const inner = payload?.data ?? payload ?? {};
      await refreshPhoneNumbers();
      setInstantDeleteTarget(null);
      setInstantDeleteInput("");
      setPhoneSettingsModal({ isOpen: false, phone: null });
      toast({
        title: inner.released ? "Number released" : "Release started",
        description: inner.released
          ? "It no longer rings and will not renew."
          : "The number will finish releasing within 24 hours.",
      });
    } catch (error: any) {
      toast({
        title: "Could not release the number",
        description: error?.response?.data?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsInstantDeleting(false);
    }
  };

  const handleRecoverPhoneNumber = async (phoneId: string, phone: any) => {
    // DEMO SANDBOX: recovery re-purchases the number from Twilio and re-bills it.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Recovering a phone number is disabled in the demo sandbox. Start a free trial to manage your real numbers.",
        }),
      );
      return;
    }

    // Stripe checkout — blocked in the native app like every other purchase
    // (this was the ONE unguarded checkout left in Manage Numbers).
    if (!allowCheckout("area-code")) return;

    setIsRecovering(phoneId);
    try {
      // Create Stripe checkout session for phone number recovery
      const response = await handleApiCall<{ url: string }>(async () => {
        const res = await api.post({
          url: getApiUrl(endpoint.stripe.checkoutSessions),
          body: {
            items: [{
              type: 'phone_number_recovery',
              phoneNumberId: phoneId,
              areaCode: phone.areaCode,
              state: phone.state,
              phoneNumber: phone.phoneNumber
            }],
            successUrl: `${window.location.origin}/settings?tab=area-codes&status=recovery_success`,
            cancelUrl: `${window.location.origin}/settings?tab=area-codes&status=recovery_cancelled`
          },
          auth: true
        });
        return res;
      });

      if (response && typeof response === 'object' && 'url' in response && response.url) {
        // Redirect to Stripe checkout
        window.location.href = response.url as string;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error: any) {

      toast({
        title: "Recovery Failed",
        description: error.response?.data?.message || "Failed to create checkout session. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRecovering(null);
    }
  };

  const handleTransferOwnership = async () => {
    // DEMO SANDBOX: transfer moves a paid number (and its Stripe line item) to another account — a real,
    // cross-account billing change that the seat reset cannot undo.
    if (isDemoMode()) {
      window.dispatchEvent(
        new CustomEvent('demo-restricted', {
          detail:
            "Transferring number ownership is disabled in the demo sandbox. Start a free trial to manage your real numbers.",
        }),
      );
      return;
    }

    if (!selectedTransferAgent || !phoneSettingsModal.phone?.id) {
      toast({
        title: "Invalid Selection",
        description: "Please select an agent to transfer to.",
        variant: "destructive",
      });
      return;
    }

    setIsTransferring(true);
    try {
      await handleApiCall(async () => {
        return await api.put({
          url: getApiUrl(`/phone-numbers/${phoneSettingsModal.phone?.id}/transfer`),
          body: {
            newOwnerId: selectedTransferAgent
          },
          auth: true
        });
      });

      toast({
        title: "Transfer Successful",
        description: "Phone number ownership has been transferred successfully.",
      });

      // Reset state
      setShowTransferDropdown(false);
      setSelectedTransferAgent('');
      setPhoneSettingsModal({ isOpen: false, phone: null });

      // Refresh phone numbers
      await refreshPhoneNumbers();
      queryClient.invalidateQueries({ queryKey: ['agencyPhoneNumbers'] });
    } catch (error: any) {

      toast({
        title: "Transfer Failed",
        description: error.response?.data?.message || "Failed to transfer phone number ownership. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsTransferring(false);
    }
  };

  // Fetch phone number statistics and activity.
  // REWRITTEN (2026-07-24): the old version hit /calls?phoneNumber=X — a filter
  // the backend never supported — so every number showed the user's SAME recent
  // calls. The dedicated number-stats endpoint returns genuinely per-number
  // data (outbound calls from the number); SMS isn't attributable per-number
  // in the schema, so it comes back null and renders as untracked.
  const fetchPhoneNumberStats = async (phoneNumber: string) => {
    setLoadingPhoneStats(true);
    setLoadingActivity(true);

    try {
      const res: any = await api.get({
        url: getApiUrl(`/communication/phone-numbers/number-stats?number=${encodeURIComponent(phoneNumber)}`),
        auth: true,
      });
      const data = res?.data?.data ?? res?.data ?? null;
      if (data && typeof data.totalCalls === 'number') {
        setPhoneStats({
          totalCalls: data.totalCalls,
          totalMinutes: data.totalMinutes ?? 0,
          avgCallDuration: data.avgCallDuration ?? 0,
          totalSmsSent: data.totalSmsSent,       // null = not tracked per number
          totalSmsReceived: data.totalSmsReceived,
        });
        setRecentCalls(Array.isArray(data.recentCalls) ? data.recentCalls : []);
      } else {
        setPhoneStats({ totalCalls: 0, totalMinutes: 0, avgCallDuration: 0, totalSmsSent: null, totalSmsReceived: null });
        setRecentCalls([]);
      }
      setRecentSms([]);
    } catch (error) {

      setPhoneStats({
        totalCalls: 0,
        totalMinutes: 0,
        avgCallDuration: 0,
        totalSmsSent: null,
        totalSmsReceived: null
      });
      setRecentCalls([]);
      setRecentSms([]);
    } finally {
      setLoadingPhoneStats(false);
      setLoadingActivity(false);
    }
  };

  // Handle modal open - Fetch phone number details and show modal
  const handleOpenPhoneSettings = async (phone: PhoneNumber) => {
    try {
      setLoadingPhoneStats(true);
      setLoadingActivity(true);

      const apiUrl = getApiUrl(`/communication/phone-numbers/${phone.id}/details`);
      const response = await handleApiCall(async () =>
        await api.get<{ statusCode: number; data: any }>({
          url: apiUrl
        })
      );

      // The API returns data directly in response, not nested under response.data
      if (response) {
        const details = response;

        // Update phone stats
        if (details.usageStats) {
          setPhoneStats({
            totalCalls: details.usageStats.totalCalls || 0,
            totalMinutes: details.usageStats.totalCallMinutes || 0,
            avgCallDuration: Math.round(details.usageStats.averageCallDuration || 0), // in seconds
            totalSmsSent: details.usageStats.totalSMSSent || 0,
            totalSmsReceived: details.usageStats.totalSMSReceived || 0,
            lastCallDate: details.phoneInfo?.lastActivityAt || null,
          });
        }

        // Update billing info
        if (details.billingInfo) {
          setPhoneBillingInfo({
            daysRemaining: details.billingInfo.daysRemaining || 0,
            monthlyCost: details.billingInfo.monthlyCost || 0,
            nextBillingDate: details.billingInfo.nextBillingDate || null,
            totalSpent: details.billingInfo.totalSpent || 0,
            isFreeTrialNumber: details.billingInfo.isFreeTrialNumber || false,
            freeTrialExpiresAt: details.billingInfo.freeTrialExpiresAt || null,
          });
        }

        // Update recent activity
        if (details.recentActivity && Array.isArray(details.recentActivity)) {
          const calls = details.recentActivity.filter((a: any) => a.type === 'call');
          const sms = details.recentActivity.filter((a: any) => a.type === 'sms');
          setRecentCalls(calls);
          setRecentSms(sms);
        }
      }

      // Open modal - React will batch state updates automatically
      setPhoneSettingsModal({ isOpen: true, phone });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load phone number details. Please try again.',
        variant: 'destructive',
      });

      // Set empty data on error
      setPhoneStats({
        totalCalls: 0,
        totalMinutes: 0,
        avgCallDuration: 0,
        totalSmsSent: 0,
        totalSmsReceived: 0,
        lastCallDate: null,
      });
      setRecentCalls([]);
      setRecentSms([]);

      // Still open modal even on error
      setPhoneSettingsModal({ isOpen: true, phone });
    } finally {
      setLoadingPhoneStats(false);
      setLoadingActivity(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading area code analysis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* InsuraBot Armor Shield */}
          <div className="relative flex-shrink-0">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
              <Bot className="h-2.5 w-2.5 text-white" />
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-900">Area Code Management</h2>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-primary/15 text-primary border border-primary/20">
                <Zap className="h-2.5 w-2.5" />
                INSURABOT ARMOR
              </span>
            </div>
            <p className="text-gray-600">
              {isNativeApp
                ? "Manage your phone numbers — protected by AI"
                : "Purchase area codes and manage your phone numbers — protected by AI"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Hidden in the native app: purchase-labeled billing tooling */}
          {!isNativeApp && (
          <button
            onClick={async () => {
              // DEMO SANDBOX: "Verify Purchases" reconciles against the LIVE Stripe
              // account — it re-reads real sessions and can provision or cancel numbers off
              // the back of them. A sandbox visitor must never touch that reconciliation,
              // and there is nothing of theirs to verify anyway.
              if (isDemoMode()) {
                window.dispatchEvent(
                  new CustomEvent('demo-restricted', {
                    detail:
                      "Verifying purchases is disabled in the demo sandbox. Start a free trial to manage real billing.",
                  }),
                );
                return;
              }
              try {
                const response = await api.post<any>({
                  url: getApiUrl(endpoint.stripe.verifyPurchases),
                  body: {},
                });
                const data = response?.data;
                // Toast for the glance, panel for the detail.
                setVerifyError(null);
                setVerifyResult(data ?? null);
                setVerifyOpen(true);
                if (data?.provisioned?.length > 0) {
                  toast({ title: "Purchases Verified", description: data.summary });
                  handleRefresh();
                } else if (data?.duplicateSubscriptions?.length > 0) {
                  for (const dup of data.duplicateSubscriptions) {
                    toast({ title: "Duplicate Detected", description: `Area code ${dup.areaCode}: ${dup.count} subs but only ${dup.dbNumbers} numbers`, variant: "destructive" });
                  }
                } else if (data?.subscriptionFix?.fixed) {
                  toast({ title: "Plan Corrected", description: `Account upgraded to ${data.subscriptionFix.stripePlan}` });
                } else if (data?.webhookHealth?.reEnabled) {
                  toast({ title: "Webhook Restored", description: "Payment processing was restored automatically." });
                } else {
                  toast({ title: "All Good", description: data?.summary || "All purchases are up to date." });
                }
              } catch {
                setVerifyResult(null);
                setVerifyError("We could not reach the billing service. Please try again in a moment.");
                setVerifyOpen(true);
                toast({ title: "Verification Failed", description: "Please try again.", variant: "destructive" });
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 transition-colors"
            title="Check for paid area codes that weren't provisioned"
          >
            <ShieldCheck className="h-4 w-4" />
            Verify Purchases
          </button>
          )}
          <Button onClick={handleRefresh} disabled={refreshing} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={areaCodeTab} onValueChange={handleAreaCodeTabChange} className="space-y-6">
        <TabsList className="inline-flex w-full" data-tour="area-codes-tabs">
          <TabsTrigger value="management" className="flex-1" data-tour="area-codes-tab-manage">Manage Numbers</TabsTrigger>
          <TabsTrigger value="purchase" className="flex-1" data-tour="area-codes-tab-purchase">Buy Area Codes</TabsTrigger>
          <TabsTrigger value="health" className="flex-1" data-tour="area-codes-tab-health">Numbers Health</TabsTrigger>
        </TabsList>

        <TabsContent value="management" className="space-y-6">
          {isNativeApp ? (
            /* Native app: number management is web-only per store-safety call —
               same instruction-only card as the Buy tab, no link-out. */
            <Card className="p-8 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Phone className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Phone Number Management</h3>
              <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                Phone number management is available on the web version of
                InsuraCentral. To manage your numbers, log in on your computer
                at app.insuracentral.com.
              </p>
            </Card>
          ) : (
            <>
          {/* Pricing Information Card */}
          <PhoneNumberPricingDisplay
            showAddButton={false}
          />

          {/* Manage Numbers Card */}
          <Card data-tour="area-codes-manage-numbers">
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  Manage Numbers ({phoneNumbers.length})
                </CardTitle>
                <CardDescription>
                  Your owned numbers and numbers assigned to you
                </CardDescription>
              </div>
            </CardHeader>
            
            <CardContent>
              {/* Payment Issues Alert Banner */}
              {phoneNumbers.some((phone: any) => phone.hasPaymentIssue) && (
                <Alert variant="destructive" className="mb-4">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Payment Required</AlertTitle>
                  <AlertDescription>
                    One or more phone numbers have payment issues. Update your payment method to avoid service interruption.
                  </AlertDescription>
                </Alert>
              )}

              {isPhoneNumbersLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="h-16 bg-gray-200 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : phoneNumbers.length === 0 ? (
                <div className="text-center py-8">
                  <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No phone numbers</h3>
                  <p className="text-gray-500">
                    You don't have any phone numbers assigned yet. Purchase area codes to get started.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {bulkSelectableNumbers.length > 0 && (
                    <div className="flex items-center justify-between gap-2 px-1">
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
                        <input type="checkbox" checked={allBulkSelected} onChange={toggleSelectAllNumbers} className="w-4 h-4 rounded border-gray-300" />
                        {selectedNumberIds.size > 0 ? `${selectedNumberIds.size} selected` : `Select all (${bulkSelectableNumbers.length})`}
                      </label>
                      {selectedNumberIds.size > 0 && (
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedNumberIds(new Set())} disabled={isBulkRemoving}>Clear</Button>
                          <Button variant="destructive" size="sm" onClick={() => setBulkRemoveConfirm(true)} disabled={isBulkRemoving}>
                            <Trash2 className="h-4 w-4 mr-1" />
                            {isBulkRemoving ? "Removing…" : `Remove ${selectedNumberIds.size} selected`}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  {phoneNumbers.length > 0 && (
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input value={mySearch} onChange={(e) => setMySearch(e.target.value)} placeholder="Search your numbers — digits, area code, state…" className="pl-8 h-8 text-sm" />
                    </div>
                  )}
                  <div className="space-y-3 max-h-[30rem] overflow-y-auto pr-1">
                    {phoneNumbers.filter((p: any) => numberMatches(p, mySearch)).map((phone: any, index: number) => (
                    <div
                      key={`${phone.id || 'phone'}-${index}`}
                      className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                        phone.hasPaymentIssue
                          ? 'border-red-300 bg-red-50 hover:bg-red-100'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        {canBulkSelect(phone) && (
                          <input
                            type="checkbox"
                            checked={selectedNumberIds.has(phone.id)}
                            onChange={() => toggleNumberSelect(phone.id)}
                            className="w-4 h-4 rounded border-gray-300 flex-shrink-0"
                            aria-label={`Select ${phone.number || phone.phoneNumber}`}
                          />
                        )}
                        <div className="flex-shrink-0">
                          <Phone className={`h-5 w-5 ${phone.hasPaymentIssue ? 'text-red-500' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              {formatPhoneNumber(phone.number || phone.phoneNumber || `Phone ${index + 1}`)}
                            </p>
                            {dialerBadge(phone.provider)}
                            {phone.hasPaymentIssue && (
                              <Badge variant="destructive" className="text-xs">
                                Payment Required
                              </Badge>
                            )}
                            {phone.isFreeTrialNumber && !phone.hasPaymentIssue && (
                              <Badge variant="outline" className="text-xs text-blue-600 border-blue-600">
                                Free Trial
                              </Badge>
                            )}
                            {phone.isActive === false && !phone.hasPaymentIssue && (
                              <Badge variant="destructive" className="text-xs">
                                Inactive
                              </Badge>
                            )}
                          </div>
                          {/* div, not p: AreaCodeSubstitutionNote's tooltip renders an
                              un-portalled <div> and <p> can't contain one (hydration error) */}
                          <div className="text-sm text-gray-500">
                            Area Code: {phone.areaCode || 'Unknown'} <AreaCodeSubstitutionNote orderedAreaCode={phone.orderedAreaCode} /> • State: {phone.state || 'Unknown'}
                            {(isAgencyOwner || isInvitedAgent) && phone.assignedTo && (
                              <span className="ml-2 text-blue-600">
                                • Assigned to: {phone.assignedUser?.name || phone.assignedUser?.email || 'Unknown Agent'}
                              </span>
                            )}
                          </div>
                          {phone.hasPaymentIssue && (
                            <p className="text-xs text-red-600 mt-1">
                              Update your payment method to keep this number active
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {phone.hasPaymentIssue && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={async () => {
                              try {
                                const { getSupabaseClient } = await import('@/app/_module/services/supabaseClient');
                                const supabase = await getSupabaseClient();
                                const { data: { session } } = await supabase.auth.getSession();

                                if (!session?.access_token) {
                                  toast({
                                    title: "Error",
                                    description: "Please sign in to update your payment method.",
                                    variant: "destructive",
                                  });
                                  return;
                                }

                                // Create Stripe Customer Portal session
                                const response = await fetch(getApiUrl('/stripe/create-portal-session'), {
                                  method: 'POST',
                                  headers: {
                                    'Authorization': `Bearer ${session.access_token}`,
                                    'Content-Type': 'application/json',
                                  },
                                  body: JSON.stringify({
                                    returnUrl: window.location.href,
                                  }),
                                });

                                const data = await response.json();

                                if (data.url) {
                                  // Mark that we opened billing portal for sync on return
                                  sessionStorage.setItem('billingPortalOpened', 'true');
                                  window.location.href = data.url;
                                } else {
                                  throw new Error('No portal URL returned');
                                }
                              } catch (error) {
                                console.error('Error opening billing portal:', error);
                                toast({
                                  title: "Error",
                                  description: "Failed to open billing portal. Please try again.",
                                  variant: "destructive",
                                });
                              }
                            }}
                          >
                            <DollarSign className="h-4 w-4 mr-1" />
                            Update Payment
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenPhoneSettings(phone)}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Agency Assignments Section (Owner/Admin Only) */}
          {isAgencyOwner && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Phone className="h-5 w-5" />
                      {showAgencyView ? 'Agency Numbers' : `Agency Assignments (${agencyCount})`}
                    </CardTitle>
                    <CardDescription>
                      {showAgencyView
                        ? 'All phone numbers grouped by agent across your agency'
                        : 'Phone numbers assigned to agents in your agency'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant={showAgencyView ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowAgencyView(!showAgencyView)}
                      className={showAgencyView ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-gray-600"}
                    >
                      <Users className="h-4 w-4 mr-1.5" />
                      {showAgencyView ? 'Viewing All Agency Numbers' : 'Show All Agency Numbers'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {showAgencyView ? (
                  /* Hierarchical view grouped by agent */
                  isAgencyNumbersLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-20 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  ) : agencyNumbersGrouped.length === 0 ? (
                    <div className="text-center py-8">
                      <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No agency members found</h3>
                      <p className="text-gray-500">No agents with phone numbers in your agency hierarchy.</p>
                    </div>
                  ) : (
                    <Accordion type="multiple" className="space-y-2">
                      {agencyNumbersGrouped.map((agent: any, index: number) => {
                        const roleColors: Record<string, string> = {
                          owner: 'bg-purple-100 text-purple-700',
                          admin: 'bg-blue-100 text-blue-700',
                          agent: 'bg-green-100 text-green-700',
                          viewer: 'bg-gray-100 text-gray-600',
                        };
                        const avatarColors: Record<string, string> = {
                          owner: 'bg-purple-500',
                          admin: 'bg-blue-500',
                          agent: 'bg-green-500',
                          viewer: 'bg-gray-400',
                        };
                        const initial = (agent.user?.name || agent.user?.email || '?')[0].toUpperCase();

                        return (
                          <AccordionItem
                            key={`${agent.user?.id || 'agent'}-${agent.agencyId || index}`}
                            value={`${agent.user?.id || 'agent'}-${agent.agencyId || index}`}
                            className="border rounded-lg px-1"
                          >
                            <AccordionTrigger className="hover:no-underline py-3 px-3">
                              <div className="flex items-center gap-3 w-full">
                                <div className={`h-9 w-9 rounded-full ${avatarColors[agent.role] || 'bg-gray-400'} flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}>
                                  {initial}
                                </div>
                                <div className="flex-1 text-left min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium text-gray-900 truncate">
                                      {agent.user?.name || 'Unknown'}
                                    </span>
                                    <Badge variant="outline" className={`text-xs ${roleColors[agent.role] || 'bg-gray-100 text-gray-600'}`}>
                                      {agent.role?.charAt(0).toUpperCase() + agent.role?.slice(1)}
                                    </Badge>
                                    <Badge variant="outline" className="text-xs bg-gray-50 text-gray-500">
                                      {agent.numbers.length} {agent.numbers.length === 1 ? 'number' : 'numbers'}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-sm text-gray-500 truncate">{agent.user?.email}</span>
                                    {agent.agencyName && (
                                      <span className="text-xs text-gray-400">• {agent.agencyName}</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-3 pb-3">
                              {agent.numbers.length === 0 ? (
                                <p className="text-sm text-gray-400 py-2 pl-12">No phone numbers</p>
                              ) : (
                                <div className="space-y-2 pl-12">
                                  {agent.numbers.map((num: any, ni: number) => (
                                    <div
                                      key={num.id || ni}
                                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                                    >
                                      <div className="flex items-center gap-3">
                                        <Phone className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        <div>
                                          <p className="font-medium text-sm">
                                            {formatPhoneNumber(num.phoneNumber || `Phone ${ni + 1}`)}
                                          </p>
                                          <div className="text-xs text-gray-500">
                                            Area Code: {num.areaCode || 'N/A'} <AreaCodeSubstitutionNote orderedAreaCode={num.orderedAreaCode} /> • {num.state || 'Unknown'}
                                          </div>
                                        </div>
                                      </div>
                                      <Badge
                                        variant="outline"
                                        className={`text-xs ${num.type === 'owned' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}
                                      >
                                        {num.type === 'owned' ? 'Owned' : 'Assigned'}
                                      </Badge>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </AccordionContent>
                          </AccordionItem>
                        );
                      })}
                    </Accordion>
                  )
                ) : (
                  /* Flat list view (original) */
                  isAgencyLoading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-16 bg-gray-200 rounded"></div>
                        </div>
                      ))}
                    </div>
                  ) : agencyCount === 0 ? (
                    <div className="text-center py-8">
                      <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No assigned numbers</h3>
                      <p className="text-gray-500">No phone numbers have been assigned to agents yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {assignedAgencyNumbers.length > 0 && (
                        <div className="relative mb-2">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                          <Input value={agencySearch} onChange={(e) => setAgencySearch(e.target.value)} placeholder="Search assigned numbers — number, agent name…" className="pl-8 h-8 text-sm" />
                        </div>
                      )}
                      <div className="space-y-3 max-h-[30rem] overflow-y-auto pr-1">
                        {assignedAgencyNumbers.filter((p: any) => numberMatches(p, agencySearch)).map((phone: any, index: number) => (
                        <div
                          key={`${phone.id || 'assigned'}-${index}`}
                          className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className="flex-shrink-0">
                              <Phone className="h-5 w-5 text-gray-400" />
                            </div>
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                {formatPhoneNumber(phone.number || phone.phoneNumber || `Phone ${index + 1}`)}
                                {dialerBadge(phone.provider)}
                              </p>
                              <div className="text-sm text-gray-500">
                                Area Code: {phone.areaCode || 'Unknown'} <AreaCodeSubstitutionNote orderedAreaCode={phone.orderedAreaCode} /> • State: {phone.state || 'Unknown'}
                              </div>
                              <p className="text-sm text-blue-600 mt-1">
                                Assigned to: {phone.assignedUser?.name || phone.assignedUser?.email || 'Unknown Agent'}
                                {phone.agency?.name && (
                                  <span className="text-gray-500 ml-1">
                                    • Agency: {phone.agency.name}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenPhoneSettings(phone)}
                            >
                              <Settings className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      </div>
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          )}

          {/* Deactivated Phone Numbers */}
          <Card data-tour="area-codes-deactivated">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-orange-500" />
                Deactivated Numbers ({deactivatedNumbers.length})
              </CardTitle>
              <CardDescription>
                Phone numbers that have expired and can be reactivated
              </CardDescription>
            </CardHeader>
            <CardContent>
              {deactivatedNumbers.length === 0 ? (
                <div className="text-center py-8">
                  <Phone className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No deactivated numbers</h3>
                  <p className="text-gray-500">
                    Phone numbers that expire will appear here and can be reactivated by paying for them.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {deactivatedNumbers.length > 0 && (
                    <div className="relative mb-2">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input value={deactivatedSearch} onChange={(e) => setDeactivatedSearch(e.target.value)} placeholder="Search deactivated numbers…" className="pl-8 h-8 text-sm" />
                    </div>
                  )}
                  <div className="space-y-3 max-h-[30rem] overflow-y-auto pr-1">
                    {deactivatedNumbers.filter((p: any) => numberMatches(p, deactivatedSearch)).map((phone: any, index: number) => (
                    <div
                      key={`${phone.id || 'deactivated'}-${index}`}
                      className="flex items-center justify-between p-4 border border-orange-200 rounded-lg bg-orange-50"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <Phone className="h-5 w-5 text-orange-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">
                              {formatPhoneNumber(phone.phoneNumber || `Phone ${index + 1}`)}
                            </p>
                            <Badge variant="outline" className="text-xs border-orange-500 text-orange-600">
                              Deactivated
                            </Badge>
                          </div>
                          <div className="text-sm text-gray-600">
                            Area Code: {phone.areaCode || 'Unknown'} <AreaCodeSubstitutionNote orderedAreaCode={phone.orderedAreaCode} /> • State: {phone.state || 'Unknown'}
                            {phone.deactivatedAt && (
                              <span className="ml-2">
                                • Deactivated: {new Date(phone.deactivatedAt).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {phone.assignedUser && (
                            <p className="text-sm text-blue-600 mt-1">
                              Assigned to: {phone.assignedUser.name || phone.assignedUser.email}
                            </p>
                          )}
                        </div>
                      </div>

                      <Button
                        onClick={() => handleRecoverPhoneNumber(phone.id, phone)}
                        disabled={isRecovering === phone.id}
                        size="sm"
                        className="bg-orange-600 hover:bg-orange-700"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${isRecovering === phone.id ? 'animate-spin' : ''}`} />
                        {isRecovering === phone.id ? 'Processing...' : 'Recover'}
                      </Button>
                    </div>
                  ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="purchase" className="space-y-6">
          {isNativeApp ? (
            /* Native app: no browsable purchase surface at all (Apple 3.1.1 /
               Play payments). Wording mirrors WebVersionModal — never mention
               buying or pricing. */
            <Card className="p-8 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
                <Phone className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground">Phone Number Management</h3>
              <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
                Phone number setup is available on the web version of InsuraCentral.
                To browse area codes and set up new numbers, log in on your
                computer at app.insuracentral.com.
              </p>
            </Card>
          ) : (
            <>
          {/* AI Area Code Matching Strategies */}
          <Card className="overflow-hidden border border-primary/20 shadow-md" data-tour="area-codes-ai-matching">
            {/* Dark tactical header */}
            <div className="bg-gradient-to-br from-primary via-primary/90 to-primary/80 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white/10 ring-1 ring-white/20 flex-shrink-0">
                  <Crosshair className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-base tracking-tight">AI Area Code Matching Strategies</h3>
                  <p className="text-black text-xs mt-0.5">
                    Select how AI matches caller ID area codes to leads for maximum local appearance. If no strategy is selected, Nearest is used by default.
                  </p>
                </div>
              </div>
            </div>

            <CardContent className="p-0">
              {isLoadingPreferences ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-700"></div>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {/* Default / Always-on: Nearest */}
                  <div className="px-6 py-4 bg-emerald-50/70">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 p-1.5 rounded-full bg-emerald-100 flex-shrink-0">
                        <Crosshair className="h-3.5 w-3.5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900">AI Nearest Area Code Matching</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase">
                            Always Active
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Default strategy. Uses geographic distance for best local appearance — active whenever no other strategy is selected.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* AI Metropolitan Matching */}
                  <div className={`px-6 py-4 transition-colors ${matchingStrategy === 'metropolitan' ? 'bg-blue-50/40' : 'bg-white hover:bg-gray-50/60'}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`mt-0.5 p-1.5 rounded-full flex-shrink-0 transition-colors ${matchingStrategy === 'metropolitan' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <MapPin className={`h-3.5 w-3.5 transition-colors ${matchingStrategy === 'metropolitan' ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <Label htmlFor="metro-strategy" className="text-sm font-semibold text-gray-900 cursor-pointer">
                            AI Metropolitan Matching
                          </Label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Matches within the same metropolitan area (all NYC codes: 212, 646, 718, 917). Ideal for targeting major metro regions.
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="metro-strategy"
                        checked={matchingStrategy === 'metropolitan'}
                        onCheckedChange={(checked) => handleStrategyToggle(checked ? 'metropolitan' : 'nearest')}
                        disabled={isSavingPreferences}
                        className="flex-shrink-0"
                      />
                    </div>
                  </div>

                  {/* AI Exact Area Code Matching */}
                  <div className={`px-6 py-4 transition-colors ${matchingStrategy === 'exact' ? 'bg-blue-50/40' : 'bg-white hover:bg-gray-50/60'}`}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className={`mt-0.5 p-1.5 rounded-full flex-shrink-0 transition-colors ${matchingStrategy === 'exact' ? 'bg-blue-100' : 'bg-gray-100'}`}>
                          <Target className={`h-3.5 w-3.5 transition-colors ${matchingStrategy === 'exact' ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                        <div>
                          <Label htmlFor="exact-strategy" className="text-sm font-semibold text-gray-900 cursor-pointer">
                            AI Exact Area Code Matching
                          </Label>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Only matches when you have the exact same area code as the lead. Most restrictive but ensures perfect matches.
                          </p>
                        </div>
                      </div>
                      <Switch
                        id="exact-strategy"
                        checked={matchingStrategy === 'exact'}
                        onCheckedChange={(checked) => handleStrategyToggle(checked ? 'exact' : 'nearest')}
                        disabled={isSavingPreferences}
                        className="flex-shrink-0"
                      />
                    </div>
                  </div>

                  {/* Active strategy footer bar */}
                  <div className="px-6 py-3 bg-primary/5 flex items-center gap-2">
                    <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                    <span className="text-xs text-gray-500">
                      Active strategy:{' '}
                      <span className="font-semibold text-gray-800">
                        {matchingStrategy === 'nearest' && 'AI Nearest Area Code Matching (Default)'}
                        {matchingStrategy === 'metropolitan' && 'AI Metropolitan Matching'}
                        {matchingStrategy === 'exact' && 'AI Exact Area Code Matching'}
                      </span>
                    </span>
                    {isSavingPreferences && (
                      <span className="ml-auto text-[10px] text-gray-400 flex items-center gap-1">
                        <div className="h-2.5 w-2.5 border border-gray-400 border-t-transparent rounded-full animate-spin" />
                        Saving...
                      </span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Alert>
            <AlertTitle>How Purchasing Works</AlertTitle>
            <AlertDescription className="space-y-2">
              <p>
                {isInvitedAgent
                  ? `When you purchase area codes, phone numbers in those area codes will be provisioned and added to your agency (${user?.currentAgency?.agency?.name || 'Agency'}). Billing is handled through your subscription payment method.`
                  : 'When you purchase area codes, phone numbers in those area codes will be provisioned and added to your account. Billing is handled through your subscription payment method.'
                }
              </p>
              {isAgencyOwner && (
                <p className="text-sm text-blue-700 bg-blue-50 p-2 rounded border border-blue-200">
                  <strong>A2P Verification for SMS:</strong> All agents can make and receive calls immediately. A2P verification is only needed to send SMS messages. Agents without A2P verification can only be assigned numbers for calling.
                </p>
              )}
            </AlertDescription>
          </Alert>

          {nextDialerAvailable && (
            <DialerSystemChoice value={buySystem} current={currentDialer} onChange={setBuyPick} />
          )}

          {/* Area Code Selection Grid */}
          <div data-tour="area-codes-purchase-grid">
          <AreaCodePurchaseGrid
            onAreaCodeSelect={handleAreaCodeSelect}
            selectedAreaCodes={selectedAreaCodes}
            maxSelectable={MAX_NUMBERS_PER_PURCHASE}
            isPurchasing={isPurchasing !== null}
            onPurchase={handleAreaCodePurchase}
            onBulkCheckout={handleBulkAreaCodeCheckout}
            isInvitedAgent={isInvitedAgent}
            agencyName={user?.currentAgency?.agency?.name}
            isAgencyOwner={isAgencyOwner}
          />
          </div>

          {/* Smart Area Code Insights */}
          {insights && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Numbers</CardTitle>
                  <Phone className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{insights.coverageAnalysis.totalNumbers}</div>
                  <p className="text-xs text-muted-foreground">
                    Active phone numbers
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Area Codes</CardTitle>
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{insights.coverageAnalysis.uniqueAreaCodes}</div>
                  <p className="text-xs text-muted-foreground">
                    Unique area codes
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">States Covered</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{insights.coverageAnalysis.statesCovered}</div>
                  <p className="text-xs text-muted-foreground">
                    States with coverage
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Coverage Score</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{insights.coverageAnalysis.coverageScore}%</div>
                  <p className="text-xs text-muted-foreground">
                    Market coverage
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* High Priority Recommendations */}
          {highPriorityRecs.length > 0 && (
            <Card className="overflow-hidden border border-primary/20 shadow-md">
              {/* Dark tactical header */}
              <div className="bg-gradient-to-br from-primary via-primary/90 to-primary/80 px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10 ring-1 ring-white/20 flex-shrink-0">
                    <Crosshair className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base tracking-tight">AI Area Code Recommendations</h3>
                    <p className="text-white/70 text-xs mt-0.5">
                      High-priority area codes to purchase based on your call patterns
                    </p>
                  </div>
                </div>
              </div>

              <CardContent className="p-0">
                <div className="divide-y divide-gray-100">
                  {highPriorityRecs.map((rec) => (
                    <div key={rec.areaCode} className="px-6 py-4 flex items-center justify-between gap-4 hover:bg-gray-50/60 transition-colors">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="mt-0.5 p-1.5 rounded-full bg-red-100 flex-shrink-0">
                          <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-gray-900">
                              ({rec.areaCode}) {rec.state}{rec.city && ` — ${rec.city}`}
                            </span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide bg-red-100 text-red-700 border border-red-200 uppercase">
                              High Priority
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {rec.reason} · {rec.frequency} calls · Last: {new Date(rec.lastCallDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">${rec.estimatedCost.toFixed(2)}</p>
                          <p className="text-[10px] text-gray-400">per month</p>
                        </div>
                        <Button
                          onClick={() => handlePurchaseAreaCode(rec.areaCode)}
                          disabled={isPurchasing === rec.areaCode}
                          size="sm"
                          className="h-8 text-xs"
                        >
                          <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
                          {isPurchasing === rec.areaCode ? 'Purchasing...' : 'Purchase'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* General Recommendations */}
          {recommendations && (
            <Card className="overflow-hidden border border-primary/20 shadow-md">
              {/* Dark tactical header */}
              <div className="bg-gradient-to-br from-primary via-primary/90 to-primary/80 px-6 py-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-white/10 ring-1 ring-white/20 flex-shrink-0">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-base tracking-tight">Coverage Action Plan</h3>
                    <p className="text-white/70 text-xs mt-0.5">
                      Action items to improve your area code coverage
                    </p>
                  </div>
                </div>
              </div>

              <CardContent className="p-0">
                <div className="divide-y divide-gray-100">
                  <div className="px-6 py-4">
                    <p className="text-sm text-gray-600">{recommendations.summary}</p>
                  </div>
                  {recommendations.actionItems.map((item, index) => (
                    <div key={index} className="px-6 py-3 flex items-start gap-3 hover:bg-gray-50/60 transition-colors">
                      <div className="mt-0.5 flex-shrink-0 h-5 w-5 rounded-full bg-slate-100 flex items-center justify-center">
                        <span className="text-[10px] font-bold text-slate-600">{index + 1}</span>
                      </div>
                      <p className="text-sm text-gray-700">{item}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
            </>
          )}
        </TabsContent>

        <TabsContent value="health" className="space-y-6">
          <Suspense fallback={
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          }>
            <NumberHealthDashboard />
          </Suspense>
        </TabsContent>
      </Tabs>

      {/* Bulk Remove Confirmation Dialog */}
      <Dialog open={bulkRemoveConfirm} onOpenChange={(open) => !open && setBulkRemoveConfirm(false)}>
        <ResponsiveDialogContent size="sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Cancel {selectedNumberIds.size} number{selectedNumberIds.size !== 1 ? 's' : ''}?
            </DialogTitle>
            <DialogDescription>
              You keep using {selectedNumberIds.size !== 1 ? 'these numbers' : 'this number'} until the end of the current billing period — then {selectedNumberIds.size !== 1 ? 'they' : 'it'} auto-release{selectedNumberIds.size !== 1 ? '' : 's'} at renewal. Billing stops now, so there's no next charge. This can't be undone once released.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border border-red-200 bg-red-50/60 p-3 space-y-2">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={bulkImmediate}
                onChange={(e) => { setBulkImmediate(e.target.checked); if (!e.target.checked) setBulkImmediateConfirm(""); }}
              />
              <span>
                <span className="font-semibold text-red-700">Delete immediately instead</span>
                <span className="block text-xs text-gray-600">
                  Numbers stop ringing right now, are released back to the carrier, and can never be recovered. No refund for the remaining days.
                </span>
              </span>
            </label>
            {bulkImmediate && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-red-700">Type DELETE to confirm immediate removal:</p>
                <Input
                  autoFocus
                  placeholder="DELETE"
                  value={bulkImmediateConfirm}
                  onChange={(e) => setBulkImmediateConfirm(e.target.value)}
                />
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => { setBulkRemoveConfirm(false); setBulkImmediate(false); setBulkImmediateConfirm(""); }} disabled={isBulkRemoving}>Keep</Button>
            <Button
              variant="destructive"
              onClick={executeBulkRemoveNumbers}
              disabled={isBulkRemoving || (bulkImmediate && bulkImmediateConfirm.trim().toUpperCase() !== 'DELETE')}
            >
              {isBulkRemoving
                ? (bulkImmediate ? "Deleting…" : "Cancelling…")
                : bulkImmediate
                  ? `Delete ${selectedNumberIds.size} now`
                  : `Cancel ${selectedNumberIds.size}`}
            </Button>
          </div>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Instant Delete — typed confirmation */}
      <Dialog open={!!instantDeleteTarget} onOpenChange={(open) => { if (!open) { setInstantDeleteTarget(null); setInstantDeleteInput(""); } }}>
        <ResponsiveDialogContent size="sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {instantDeleteTarget?.phoneNumber} right now?
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  This is immediate and permanent: the number <strong>stops ringing the moment you confirm</strong>, is released back to the carrier, and can never be recovered. Billing stops too — but there&apos;s no refund for the rest of this month.
                </p>
                <p className="font-medium text-foreground">
                  Type the exact number to confirm:
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder={instantDeleteTarget?.phoneNumber || ""}
            value={instantDeleteInput}
            onChange={(e) => setInstantDeleteInput(e.target.value)}
            className={instantDeleteMatches ? "border-red-400" : ""}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => { setInstantDeleteTarget(null); setInstantDeleteInput(""); }} disabled={isInstantDeleting}>
              Keep the number
            </Button>
            <Button variant="destructive" onClick={executeInstantDelete} disabled={!instantDeleteMatches || isInstantDeleting}>
              {isInstantDeleting ? "Releasing…" : "Delete permanently"}
            </Button>
          </div>
        </ResponsiveDialogContent>
      </Dialog>

      {/* Phone Number Selection Dialog */}
      <Dialog open={showPhoneNumberSelector} onOpenChange={setShowPhoneNumberSelector}>
        <ResponsiveDialogContent size="3xl">
          <DialogHeader>
            <DialogTitle>Select Phone Number</DialogTitle>
            <DialogDescription>
              Choose a phone number from available area codes. Numbers will be provisioned and added to your account.
              {pricingInfo && !pricingInfo.userHasFreePhone && pricingInfo.additionalPhoneNumberPrice > 0 && (
                <span className="block mt-2 text-amber-600 font-medium">
                  This will cost ${pricingInfo.additionalPhoneNumberPrice}/{pricingInfo.currency.toLowerCase()} per month, billed through your subscription.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <PhoneNumberSelector
            phoneNumbers={availableNumbers}
            onSelect={handlePhoneNumberSelected}
            isLoading={isLoadingAvailable}
          />
        </ResponsiveDialogContent>
      </Dialog>

      {/* Phone Settings Modal - MASSIVELY ENHANCED Configuration View */}
      <Dialog open={phoneSettingsModal.isOpen} onOpenChange={(open) =>
        setPhoneSettingsModal({ isOpen: open, phone: null })
      }>
        <ResponsiveDialogContent size="3xl" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold">
                  {formatPhoneNumber(phoneSettingsModal.phone?.phoneNumber || (phoneSettingsModal.phone as any)?.number || '')}
                </DialogTitle>
                <DialogDescription>
                  Complete Phone Number Configuration & Management
                </DialogDescription>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Badge variant={phoneSettingsModal.phone?.isActive !== false ? "default" : "destructive"} className="text-sm">
                  {phoneSettingsModal.phone?.isActive !== false ? 'Active' : 'Inactive'}
                </Badge>
              </div>
            </div>
          </DialogHeader>

          {phoneSettingsModal.phone && (
            <div className="space-y-6">
              {/* 1. USAGE STATISTICS SECTION */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-600" />
                  Usage Statistics
                </h3>
                {loadingPhoneStats ? (
                  <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 mb-2">
                          <PhoneCall className="h-4 w-4 text-green-600" />
                          <p className="text-xs font-medium text-gray-500">Total Calls</p>
                        </div>
                        <p className="text-2xl font-bold">{phoneStats?.totalCalls || 0}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 mb-2">
                          <Clock className="h-4 w-4 text-blue-600" />
                          <p className="text-xs font-medium text-gray-500">Total Minutes</p>
                        </div>
                        <p className="text-2xl font-bold">{phoneStats?.totalMinutes || 0}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="h-4 w-4 text-purple-600" />
                          <p className="text-xs font-medium text-gray-500">Avg Duration</p>
                        </div>
                        <p className="text-2xl font-bold">
                          {Math.floor((phoneStats?.avgCallDuration || 0) / 60)}<span className="text-sm text-gray-500">m</span>{' '}
                          {(phoneStats?.avgCallDuration || 0) % 60}<span className="text-sm text-gray-500">s</span>
                        </p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 mb-2">
                          <ArrowUpRight className="h-4 w-4 text-orange-600" />
                          <p className="text-xs font-medium text-gray-500">SMS Sent</p>
                        </div>
                        <p className="text-2xl font-bold">{phoneStats?.totalSmsSent || 0}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center gap-2 mb-2">
                          <ArrowDownLeft className="h-4 w-4 text-teal-600" />
                          <p className="text-xs font-medium text-gray-500">SMS Received</p>
                        </div>
                        <p className="text-2xl font-bold">{phoneStats?.totalSmsReceived || 0}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>

              <Separator />

              {/* 2. BILLING & EXPIRATION SECTION */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-600" />
                  Billing & Expiration
                </h3>
                {loadingPhoneStats ? (
                  <div className="flex items-center justify-center py-8 bg-gray-50 rounded-lg">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gradient-to-br from-green-50 to-blue-50 p-4 rounded-lg border border-green-200">
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-600">
                        {phoneBillingInfo?.isFreeTrialNumber ? 'Trial Days Left' : 'Days Remaining'}
                      </label>
                      <p className={`text-2xl font-bold ${
                        (phoneBillingInfo?.daysRemaining ?? 0) < 0
                          ? 'text-red-600'
                          : (phoneBillingInfo?.daysRemaining ?? 0) < 7
                          ? 'text-yellow-600'
                          : 'text-green-600'
                      }`}>
                        {phoneBillingInfo?.daysRemaining === null
                          ? 'N/A'
                          : (phoneBillingInfo?.daysRemaining ?? 0) < 0
                          ? 'Expired'
                          : phoneBillingInfo?.daysRemaining}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(phoneBillingInfo?.daysRemaining ?? 0) < 0
                          ? 'subscription expired'
                          : phoneBillingInfo?.isFreeTrialNumber
                          ? 'free trial'
                          : 'until next billing'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-600">Monthly Cost</label>
                      <p className="text-2xl font-bold text-blue-600">
                        ${phoneBillingInfo?.monthlyCost?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-gray-500">per month</p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-600">Next Billing Date</label>
                      <p className="text-lg font-semibold text-gray-800">
                        {phoneBillingInfo?.nextBillingDate ? new Date(phoneBillingInfo.nextBillingDate).toLocaleDateString() : 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {(phoneBillingInfo?.daysRemaining ?? 0) < 0
                          ? 'billing overdue'
                          : phoneBillingInfo?.isFreeTrialNumber
                          ? 'trial expires'
                          : 'auto-renew'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-xs font-medium text-gray-600">Total Spent</label>
                      <p className="text-2xl font-bold text-purple-600">
                        ${phoneBillingInfo?.totalSpent?.toFixed(2) || '0.00'}
                      </p>
                      <p className="text-xs text-gray-500">lifetime</p>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* 3. RECENT ACTIVITY SECTION (TABS) */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-orange-600" />
                  Recent Activity
                </h3>
                <Tabs value={activityTab} onValueChange={handleActivityTabChange} className="w-full">
                  <TabsList className="inline-flex w-full">
                    <TabsTrigger value="calls" className="flex-1">Recent Calls</TabsTrigger>
                    <TabsTrigger value="sms" className="flex-1">Recent SMS</TabsTrigger>
                  </TabsList>

                  <TabsContent value="calls" className="space-y-1 mt-2">
                    {loadingActivity ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      </div>
                    ) : recentCalls.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <PhoneCall className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No call history yet</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {recentCalls.map((call: any, idx: number) => {
                          const isInbound = call.direction === 'inbound' || call.direction === 'incoming';
                          const callStatusMap: Record<string, { label: string; className: string }> = {
                            answered:     { label: "Answered",   className: "bg-green-600 text-white border-transparent" },
                            completed:    { label: "Answered",   className: "bg-green-600 text-white border-transparent" },
                            missed:       { label: "Missed",     className: "bg-red-100 text-red-700 border-transparent" },
                            declined:     { label: "Declined",   className: "bg-red-100 text-red-700 border-transparent" },
                            failed:       { label: "Failed",     className: "bg-red-100 text-red-700 border-transparent" },
                            busy:         { label: "Busy",       className: "bg-gray-100 text-gray-700 border-transparent" },
                            voicemail:    { label: "Voicemail",  className: "bg-gray-100 text-gray-700 border-transparent" },
                            "no-answer":  { label: "No Answer",  className: "border border-gray-300 text-gray-600" },
                            "no_answer":  { label: "No Answer",  className: "border border-gray-300 text-gray-600" },
                            canceled:     { label: "Canceled",   className: "border border-gray-300 text-gray-600" },
                            initiated:    { label: "Initiated",  className: "border border-gray-300 text-gray-600" },
                            "in-progress":{ label: "In Progress",className: "bg-gray-100 text-gray-700 border-transparent" },
                            ringing:      { label: "Ringing",    className: "bg-gray-100 text-gray-700 border-transparent" },
                            queued:       { label: "Queued",     className: "border border-gray-300 text-gray-600" },
                          };
                          const statusInfo = callStatusMap[call.status] ?? { label: call.status || 'Unknown', className: "border border-gray-300 text-gray-600" };
                          const dur = call.duration || 0;
                          const durStr = dur > 0
                            ? (Math.floor(dur / 60) > 0 ? `${Math.floor(dur / 60)}m ${dur % 60}s` : `${dur % 60}s`)
                            : null;
                          const callDate = call.createdAt || call.timestamp;
                          return (
                            <div key={idx} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                              <div className={`p-2 rounded-lg flex-shrink-0 ${isInbound ? 'bg-blue-50' : 'bg-green-50'}`}>
                                {isInbound
                                  ? <PhoneIncoming className="h-4 w-4 text-blue-600" />
                                  : <PhoneOutgoing className="h-4 w-4 text-green-600" />
                                }
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="font-medium text-sm text-gray-900 truncate">
                                    {formatPhoneNumber(call.contact || call.to || call.from || 'Unknown')}
                                  </p>
                                  <Badge className={`text-xs flex-shrink-0 ${statusInfo.className}`}>
                                    {statusInfo.label}
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                  <Clock className="h-3 w-3" />
                                  <span>
                                    {callDate && !isNaN(new Date(callDate).getTime())
                                      ? formatDistanceToNow(new Date(callDate), { addSuffix: true })
                                      : 'Recently'}
                                  </span>
                                  {durStr && (
                                    <>
                                      <span className="text-gray-300">•</span>
                                      <span>{durStr}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="sms" className="space-y-1 mt-2">
                    {loadingActivity ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      </div>
                    ) : recentSms.length === 0 ? (
                      <div className="text-center py-8 bg-gray-50 rounded-lg">
                        <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No SMS history yet</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {recentSms.map((sms: any, idx: number) => {
                          const isInbound = sms.direction === 'inbound';
                          const smsDate = sms.createdAt || sms.timestamp;
                          return (
                            <div key={idx} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                              <div className={`p-2 rounded-lg flex-shrink-0 ${isInbound ? 'bg-blue-50' : 'bg-purple-50'}`}>
                                <MessageSquare className={`h-4 w-4 ${isInbound ? 'text-blue-600' : 'text-purple-600'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <p className="font-medium text-sm text-gray-900 truncate">
                                    {formatPhoneNumber(sms.contact || sms.to || sms.from || 'Unknown')}
                                  </p>
                                  <span className="text-xs text-gray-500 flex-shrink-0">
                                    {isInbound ? 'Received' : 'Sent'}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                                  {sms.body || sms.message || 'No message content'}
                                </p>
                                <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                                  <Clock className="h-3 w-3" />
                                  <span>
                                    {smsDate && !isNaN(new Date(smsDate).getTime())
                                      ? formatDistanceToNow(new Date(smsDate), { addSuffix: true })
                                      : 'Recently'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>

              <Separator />

              {/* 4. PHONE NUMBER INFORMATION SECTION */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Settings className="h-5 w-5 text-gray-600" />
                  Phone Number Information
                </h3>
                <div className="space-y-4 bg-gray-50 p-4 rounded-lg border">
                  <div className="grid grid-cols-2 gap-4">
                    {/* A2P Verification Status */}
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <Shield className="h-4 w-4 text-blue-600" />
                        <Label className="text-sm font-medium">A2P Verification</Label>
                      </div>
                      {isA2PLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                          <p className="text-sm text-gray-500">Loading...</p>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="default"
                              className={
                                a2pStatus?.status === 'verified' ? 'bg-green-600' :
                                a2pStatus?.status === 'pending' ? 'bg-yellow-600' :
                                a2pStatus?.status === 'rejected' ? 'bg-red-600' :
                                'bg-gray-600'
                              }
                            >
                              {a2pStatus?.status === 'verified' ? 'Verified' :
                               a2pStatus?.status === 'pending' ? 'Pending' :
                               a2pStatus?.status === 'rejected' ? 'Rejected' :
                               'Unverified'}
                            </Badge>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {a2pStatus?.status === 'verified' ?
                              `Ready for high-volume SMS` :
                              a2pStatus?.status === 'pending' ?
                              'Verification in progress' :
                              a2pStatus?.status === 'rejected' ?
                              'Verification failed' :
                              'Standard SMS limits apply'}
                          </p>
                        </>
                      )}
                    </div>

                    {/* Assignment Status */}
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <UserCog className="h-4 w-4 text-gray-600" />
                        <Label className="text-sm font-medium">Assignment Status</Label>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {phoneSettingsModal.phone?.assignedTo?.name || phoneSettingsModal.phone?.assignedTo?.email || 'Unassigned'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {phoneSettingsModal.phone?.assignedTo ? 'Assigned to agent' : 'Available for assignment'}
                      </p>
                    </div>

                    {/* SMS Capability */}
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="h-4 w-4 text-gray-600" />
                        <Label className="text-sm font-medium">SMS Capability</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="default" className="bg-green-600">
                          Enabled
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        SMS and MMS supported
                        {a2pStatus?.messagingLimit && ` • ${a2pStatus.messagingLimit.toLocaleString()}/day limit`}
                      </p>
                    </div>

                    {/* Voice Capability */}
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <PhoneCall className="h-4 w-4 text-gray-600" />
                        <Label className="text-sm font-medium">Voice Capability</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="default" className="bg-green-600">
                          Active
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Inbound and outbound calls
                      </p>
                    </div>

                    {/* Number Type */}
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="h-4 w-4 text-gray-600" />
                        <Label className="text-sm font-medium">Number Type</Label>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        Local
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Standard phone number
                      </p>
                    </div>

                    {/* Last Activity */}
                    <div className="p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-2 mb-1">
                        <Activity className="h-4 w-4 text-gray-600" />
                        <Label className="text-sm font-medium">Last Activity</Label>
                      </div>
                      <p className="text-base font-semibold text-gray-900">
                        {phoneStats?.lastCallDate ? new Date(phoneStats.lastCallDate).toLocaleDateString() : 'No activity'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {phoneStats?.lastCallDate ? 'Most recent call' : 'No calls recorded'}
                      </p>
                    </div>
                  </div>

                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertDescription className="text-sm text-blue-800">
                      For dialer preferences like voicemail and call forwarding, visit{' '}
                      <a href="/settings?tab=account&accountTab=preferences" className="font-medium underline">
                        Account Settings → Preferences
                      </a>
                    </AlertDescription>
                  </Alert>
                </div>
              </div>

              <Separator />

              {/* 5. DANGER ZONE SECTION */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Danger Zone
                </h3>
                <div className="space-y-3 bg-red-50 p-4 rounded-lg border-2 border-red-200">
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Warning</AlertTitle>
                    <AlertDescription>
                      These actions are permanent and cannot be undone. Proceed with caution.
                    </AlertDescription>
                  </Alert>

                  {isProtectedFreeNumber(phoneSettingsModal.phone) && (
                    <div className="p-4 bg-white rounded-lg border border-blue-200">
                      <p className="font-semibold text-blue-700">Free number — protected</p>
                      <p className="text-sm text-gray-600">
                        Your free number stays active for its full 30 days. It can be removed after{' '}
                        {new Date((phoneSettingsModal.phone as any)?.freeTrialExpiresAt || (phoneSettingsModal.phone as any)?.free_trial_expires_at).toLocaleDateString('en-US')}.
                      </p>
                    </div>
                  )}

                  {!isProtectedFreeNumber(phoneSettingsModal.phone) && (
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-300">
                    <div>
                      <p className="font-semibold text-red-700">Cancel Phone Number</p>
                      <p className="text-sm text-gray-600">
                        You'll keep using this number until the end of the current billing period, then it releases at renewal. Billing stops now — no next charge.
                      </p>
                    </div>
                    <Button
                      variant="destructive"
                      onClick={() => {
                        const phoneId = phoneSettingsModal.phone?.id || (phoneSettingsModal.phone as any)?.id;
                        if (phoneId) {
                          handleRemovePhoneNumber(phoneId);
                          setPhoneSettingsModal({ isOpen: false, phone: null });
                        }
                      }}
                      disabled={isRemoving === (phoneSettingsModal.phone?.id || (phoneSettingsModal.phone as any)?.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {isRemoving === (phoneSettingsModal.phone?.id || (phoneSettingsModal.phone as any)?.id) ? 'Removing...' : 'Remove'}
                    </Button>
                  </div>
                  )}

                  {/* Delete Immediately — HIDDEN for a protected free number. This exact
                      button is how Davide Juncaj (GFG) lost his free number six hours
                      after signup. */}
                  {!isProtectedFreeNumber(phoneSettingsModal.phone) && (
                  <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-red-300">
                    <div>
                      <p className="font-semibold text-red-700">Delete Immediately</p>
                      <p className="text-sm text-gray-600">
                        Releases this number right now — it stops ringing instantly and can never be recovered. Use this for spam-flagged numbers you want nothing to do with.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="shrink-0 border-red-400 text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setInstantDeleteInput("");
                        setInstantDeleteTarget(phoneSettingsModal.phone);
                      }}
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Delete now
                    </Button>
                  </div>
                  )}

                  {isAgencyOwner && (
                    <div className="p-4 bg-white rounded-lg border border-orange-300 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-orange-700">Transfer Ownership</p>
                          <p className="text-sm text-gray-600">
                            Transfer this phone number to another A2P verified agent in your agency.
                          </p>
                        </div>
                        {!showTransferDropdown && (
                          <Button
                            variant="outline"
                            className="border-orange-400 text-orange-700 hover:bg-orange-50"
                            onClick={() => setShowTransferDropdown(true)}
                          >
                            <UserCog className="h-4 w-4 mr-2" />
                            Transfer
                          </Button>
                        )}
                      </div>

                      {showTransferDropdown && (
                        <div className="space-y-3 pt-2 border-t">
                          <div className="space-y-2">
                            <Label htmlFor="transfer-agent" className="text-sm font-medium">
                              Select Agent
                            </Label>
                            {isLoadingAgents ? (
                              <div className="flex items-center gap-2 p-3 border rounded-lg">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600"></div>
                                <p className="text-sm text-gray-500">Loading verified agents...</p>
                              </div>
                            ) : verifiedAgents.length === 0 ? (
                              <Alert className="bg-orange-50 border-orange-200">
                                <AlertDescription className="text-sm text-orange-800">
                                  No A2P verified agents available in your agency. Agents must complete A2P verification before receiving phone numbers.
                                </AlertDescription>
                              </Alert>
                            ) : (
                              <Select
                                value={selectedTransferAgent}
                                onValueChange={setSelectedTransferAgent}
                              >
                                <SelectTrigger id="transfer-agent" className="w-full">
                                  <SelectValue placeholder="Choose an agent..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {verifiedAgents.map((agent: any) => (
                                    <SelectItem key={agent.id} value={agent.id}>
                                      {agent.name || agent.email} {agent.email && agent.name && `(${agent.email})`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              className="flex-1"
                              onClick={() => {
                                setShowTransferDropdown(false);
                                setSelectedTransferAgent('');
                              }}
                              disabled={isTransferring}
                            >
                              Cancel
                            </Button>
                            <Button
                              className="flex-1 bg-orange-600 hover:bg-orange-700"
                              onClick={handleTransferOwnership}
                              disabled={!selectedTransferAgent || isTransferring}
                            >
                              {isTransferring ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                  Transferring...
                                </>
                              ) : (
                                'Confirm Transfer'
                              )}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Footer Actions */}
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setPhoneSettingsModal({ isOpen: false, phone: null })}
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    toast({
                      title: "Settings Saved",
                      description: "Phone number settings have been updated successfully.",
                    });
                  }}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </ResponsiveDialogContent>
      </Dialog>

      <Dialog open={!!substituteConfirm} onOpenChange={(o) => !o && setSubstituteConfirm(null)}>
        <ResponsiveDialogContent size="sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              {substituteConfirm?.items.length === 1
                ? "One area code is out of stock"
                : `${substituteConfirm?.items.length} area codes are out of stock`}
            </DialogTitle>
            <DialogDescription>
              We can still cover {substituteConfirm?.items.length === 1 ? "it" : "them"} with a
              number in the same metro area, at the same price. Here&rsquo;s exactly what
              you&rsquo;ll get:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 rounded-lg border bg-muted/40 p-3">
            {substituteConfirm?.items.map((it) => (
              <div key={it.areaCode} className="flex items-center gap-2 text-sm">
                <span className="font-mono font-medium">{it.areaCode}</span>
                <span className="text-muted-foreground">&rarr;</span>
                <span className="font-mono">{it.likelyAreaCodes.join(" or ")}</span>
                <span className="text-muted-foreground text-xs">(same metro)</span>
              </div>
            ))}
          </div>
          <p className="text-muted-foreground text-xs">
            Your invoice will show the area code you ordered.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setSubstituteConfirm(null)}>
              Pick different codes
            </Button>
            <Button
              onClick={() => {
                const url = substituteConfirm?.url;
                setSubstituteConfirm(null);
                setSelectedAreaCodes(new Set()); // spent — see the note on redirect
                if (url) window.location.href = url;
              }}
            >
              Continue to payment
            </Button>
          </div>
        </ResponsiveDialogContent>
      </Dialog>

      <VerifyPurchasesResult
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        data={verifyResult}
        error={verifyError}
      />
    </div>
  );
}